import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';

import { CustomerFlatNode, CustomerNode, TreeHelperService } from './tree-helper.service';

import { debounceTime, distinctUntilChanged, Observable, Subject } from 'rxjs';
import { SelectedCustomerCacheInterface } from 'src/app/core/store/interface/selectedCustomerCache.interface';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { select, Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/appState.interface';
import { getApplySelectedCustomer, getCustomersSelector } from 'src/app/core/store/selectors';
import { DashboardType } from 'src/app/pages/dashboards/models/dashboard-models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'customers-tree',
  templateUrl: './customers-tree.component.html',
  styleUrls: ['./customers-tree.component.scss']
})
export class CustomersTreeComponent implements OnInit, OnDestroy {

  @Input() isEditMode: boolean = false;
  @Input() selectedCustomers: string | string[] = [];
  @Input() isMultipleSelection: boolean = true;
  @Output() customerSelectionChange = new EventEmitter<SelectionModel<CustomerFlatNode>>();

  customers$: Observable<SelectedCustomerCacheInterface[]>;
  appliedCustomer$: Observable<SelectedCustomerCacheInterface | null>;
  treeControl: FlatTreeControl<CustomerFlatNode>;
  treeFlattener: MatTreeFlattener<CustomerNode, CustomerFlatNode>;
  dataSource: MatTreeFlatDataSource<CustomerNode, CustomerFlatNode>;
  customerSelection = new SelectionModel<CustomerFlatNode>(true);
  actualCustomerSelected: any | null = null;

  isTreeFiltered: boolean = false;
  private filterInput$ = new Subject<string>();

  // Tree helpers for Angular Material Tree (delegated to service)
  getLevel = this.treeHelper.getLevel;
  isExpandable = this.treeHelper.isExpandable;
  getChildren = this.treeHelper.getChildren;
  hasChild = this.treeHelper.hasChild;


  constructor(public treeHelper: TreeHelperService,
    private _store: Store<AppStateInterface>,
    private _translateService: TranslateService
  ) {
    // Use helper service for tree logic
    this.treeFlattener = new MatTreeFlattener(
      this.treeHelper.transformer,
      this.treeHelper.getLevel,
      this.treeHelper.isExpandable,
      this.treeHelper.getChildren
    );
    this.treeControl = new FlatTreeControl<CustomerFlatNode>(this.treeHelper.getLevel, this.treeHelper.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnInit() {
    this._translateService.use(localStorage.getItem('language') || 'en');
    this.customers$ = this._store.pipe(select(getCustomersSelector));
    this.appliedCustomer$ = this._store.pipe(select(getApplySelectedCustomer));

    this.customers$.subscribe(customers => {
      this.dataSource.data = this.treeHelper.buildTree(customers);
      setTimeout(() => {
        // Expand first root node by default
        if (this.treeControl.dataNodes.length > 0) {
          this.treeControl.expand(this.treeControl.dataNodes[0]);
        }

        this.appliedCustomer$.subscribe(customer => {
          this.actualCustomerSelected = customer;
          this.selectAndExpandCustomerNodesForEdit();
        });
      }, 0); // Wait for the tree to render
    });

    this.filterInput$.pipe(
      debounceTime(500), // Adjust delay as needed (ms)
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchTreeValue(searchValue);
    });
  }

  ngOnDestroy() {}

  descendantsAllSelected(node: CustomerFlatNode): boolean {
    return this.treeHelper.descendantsAllSelected(this.treeControl, this.customerSelection, node);
  }

  descendantsPartiallySelected(node: CustomerFlatNode): boolean {
    return this.treeHelper.descendantsPartiallySelected(this.treeControl, this.customerSelection, node);
  }

  customerSelectionToggle(node: CustomerFlatNode): void {
    this.treeHelper.customerSelectionToggle(
      this.treeControl,
      this.customerSelection,
      node,
      n => this.getParentNode(n),
      this.isTreeFiltered
    );

    this.customerSelectionChange.emit(this.customerSelection);
  }

  checkAllParentsSelection(node: CustomerFlatNode): void {
    this.treeHelper.checkAllParentsSelection(
      this.treeControl,
      this.customerSelection,
      node,
      n => this.getParentNode(n)
    );
  }

  checkRootNodeSelection(node: CustomerFlatNode): void {
    this.treeHelper.checkRootNodeSelection(this.treeControl, this.customerSelection, node);
  }

  getParentNode(node: CustomerFlatNode): CustomerFlatNode | null {
    return this.treeHelper.getParentNode(this.treeControl, node);
  }

  searchTreeValue(searchValue: string) {
    const selectedUids = this.customerSelection.selected.map(n => n.item.uid);

    this.customers$.subscribe(customers => {
      const filtered = this.treeHelper.filterTree(
        customers,
        searchValue
      );
      this.dataSource.data = filtered;
      this.treeControl.collapseAll();
      this.treeHelper.expandSearchedNodes(
        this.treeControl,
        n => this.getParentNode(n),
        searchValue
      );
    });

    // Restore selection by uid only in edit mode
    if (this.isEditMode) {
      this.customerSelection.clear();
      this.treeControl.dataNodes.forEach(node => {
        if (selectedUids.includes(node.item.uid)) {
          this.customerSelection.select(node);
        }
      });
      this.customerSelectionChange.emit(this.customerSelection);
    }
  }

  filterTree(event?: any) {
    const value = event ? event.target.value : '';
    this.isTreeFiltered = !!value;
    // Clear selection when filtering only if not in edit mode
    if (!this.isEditMode) {
      this.customerSelection.clear();
      this.customerSelectionChange.emit(this.customerSelection);
    }
    this.filterInput$.next(value);
  }

  scrollToSelectedCustomer() {
    setTimeout(() => {
      const el = document.querySelector(`[data-uid="${this.actualCustomerSelected.uid}"]`);
      const dialogContent = document.querySelector('.mat-dialog-content');
      if (el && dialogContent) {
        // Scroll the dialog content so the element is visible
        const elRect = (el as HTMLElement).getBoundingClientRect();
        const containerRect = (dialogContent as HTMLElement).getBoundingClientRect();
        const offset = elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2;
        (dialogContent as HTMLElement).scrollTop += offset;
      }
    }, 100); // Delay to ensure DOM is updated
  }

  /**
   * Expands the given node and all its parent nodes in the tree.
   */
  expandNodeAndParents(flatNode: CustomerFlatNode) {
    let parent = this.getParentNode(flatNode);
    while (parent) {
      this.treeControl.expand(parent);
      parent = this.getParentNode(parent);
    }
    if (flatNode.expandable) {
      this.treeControl.expand(flatNode);
    }
  }

  findActualCustomerNode() {
    setTimeout(() => {
      const flatNode = this.treeControl.dataNodes.find(n => n.item.uid === this.actualCustomerSelected?.uid);
      if (flatNode) {
        this.expandNodeAndParents(flatNode);
      }
    }, 10);
  }

  selectAndExpandCustomerNodesForEdit() {
    // Build a map from uid to flatNode for O(1) lookup
    const nodeMap = new Map<string, CustomerFlatNode>();
    this.treeControl.dataNodes.forEach(node => {
      if (node.item && node.item.uid) {
        nodeMap.set(node.item.uid, node);
      }
    });

    (this.selectedCustomers! as string[]).forEach(uid => {
      const flatNode = nodeMap.get(uid);
      if (flatNode) {
        this.customerSelection.select(flatNode);
        this.expandNodeAndParents(flatNode);
      }
    });

    this.customerSelectionChange.emit(this.customerSelection);
  }

  selectSingleNode(node: CustomerFlatNode) {
    this.customerSelection.clear();
    this.customerSelection.select(node);
    this.customerSelectionChange.emit(this.customerSelection);
  }
}
